<template lang="pug">
#safety-confirm-list(v-cloak)
  .box
    .box-header
      .note
        span 施設関係者（職員）への安否確認と、ご家族へのご利用者と施設状況の情報提供が出来ます。
        span.font-strong-color 施設関係者もご家族も、LINEにもメールにも送れます。
        br
        span 事前設定で宛先や安否確認内容／情報提供内容を登録しておくと便利です。
      .d-flex.flex-row.justify-content-end.align-items-center
        button.register-btn.btn.mr-2(@click="gotoSafetyConfirmRegister" type="button") 新規作成
        button.template-edit-btn.btn.mr-2(@click="gotoOtherUserSafetyConfirmTemplateEdit" type="button") 施設関係者の事前設定
        button.template-edit-btn.btn(@click="gotoFamilySafetyConfirmTemplateEdit" type="button") 家族の事前設定
    .box-body
      SafetyConfirmTable(
        v-model="items"
        :fields="fields"
        :totalRows.sync="rows"
        :currentPage="currentPage"
        :perPage="perPage"
        :isLoading="isLoading"
        @onOtherUserDetail="gotoOtherUserDetail"
        @onGotoSendOtherUserSafetyConfirm="gotoSendOtherUserSafetyConfirm"
        @onFamilyDetail="gotoFamilyDetail"
        @onDelete="confirmDelete"
      )
      .table-options.d-flex.flex-row.justify-content-end.align-items-center
        // ページあたりの行数
        span.table-option-text 1ページの行数:
        .per-page
          b-form-select(id="per-page-select" v-model="perPage" :options="pageOptions" size="md" @change="selectPerPage")
        // ページネーション
        b-pagination.m-0(v-model="currentPage" :total-rows="rows" :per-page="perPage" :disabled="isLoading" @change="selectPage" hide-goto-end-buttons)
  
  OkCancelModal(v-model="showConfirmDeleteModal" :title="confirmDeleteModalTitle" @ok="deleteSafetyConfirm")
  OkModal(v-model="showDeleteSuccessModal" title="削除に成功しました。")

  PageLoading(:isBusy="isBusy" :message="loadingMessage")
</template>

<script lang="ts">
import { Component, Inject, Watch, Vue } from "vue-property-decorator";
import { Route } from "vue-router";
import { userStore, itemListFilterStore } from "@/store/store";
import { errorHandle } from "@/errorHandler";

import Setting from "../../../../public/setting.json";
import { PcUrl } from "@/commons/constants/PcUrl";
import SafetyConfirmsService, { SafetyConfirmsGetModel } from "@/commons/services/SafetyConfirms";

import PageLoading from "@/pc/basics/PageLoading.vue";
import SafetyConfirmTable, { SafetyConfirmsTableField, SafetyConfirmsTableItem } from "@/pc/containers/safetyConfirms/SafetyConfirmsTable.vue";
import OkCancelModal from "@/pc/basics/OkCancelModal.vue";
import OkModal from "@/pc/basics/OkModal.vue";

/** ナビゲーションガードのフックを使えるようにする */
Component.registerHooks(["beforeRouteLeave"]);

/**
 * ### 安否確認一覧画面コンポーネント
 */
@Component({
  components: {
    PageLoading,
    SafetyConfirmTable,
    OkCancelModal,
    OkModal
  }
})
export default class SafetyConfirmList extends Vue {
  /** 安否確認サービス */
  @Inject("SafetyConfirmsService") safetyConfirmsService!: SafetyConfirmsService;

  /** ページ全体ローディング表示可否 */
  isBusy: boolean = false;
  /** ローディングメッセージ */
  loadingMessage: string = "";

  /** 列名 */
  fields: Array<SafetyConfirmsTableField> = [];
  /** 各行の情報 */
  items: Array<SafetyConfirmsTableItem> = [];
  /** 読込み中か否か */
  isLoading: boolean = true;

  /** ページングの現在ページ番号 */
  currentPage: number = 1;
  /** 現在取得している件数 */
  currentRows: number = 0;
  /** ページングの全体行数 */
  rows: number = 0;
  /** 1ページに表示する行数 */
  perPage: number = Number(Setting.safetyConfirm.perPage);
  /** 1ページに表示する行数の選択項目 */
  pageOptions: number[] = Setting.safetyConfirm.pageOptions;
  /** 先行して取得するページ範囲 */
  pageRange: number = Setting.safetyConfirm.pageRange;

  /** 削除確認モーダル表示可否 */
  showConfirmDeleteModal: boolean = false;
  /** 削除確認モーダルタイトル */
  confirmDeleteModalTitle: string = "";
  /** 削除対象の安否確認 */
  deleteTarget: SafetyConfirmsTableItem | null = null;
  /** 削除完了モーダル表示可否 */
  showDeleteSuccessModal: boolean = false;

  /**
   * ### 1ページあたりの表示行数の変更イベント
   *
   * - 変更をクッキーに持たせる
   */
  @Watch("perPage")
  public changePerPage(perPage: number): void {
    this.setCookie(perPage);

    return;
  }

  /**
   * ### 1ページあたりの表示行数をCookieに設定する
   */
  public setCookie(perPage: number): void {
    // Cookie有効秒数。有効期限は必ず設定しないといけないので、10年分にしておく
    const saveSecond = 10 * 365 * 24 * 60 * 60;
    document.cookie = `safety-confirm-list-per-page=${perPage}; max-age=${saveSecond};`;

    return;
  }

  /**
   * ### vue lifecycle mounted
   */
  public async mounted(): Promise<void> {
    // 画面初期表示時に作成して変更しないものを初期化する
    const fields: Array<SafetyConfirmsTableField> = this.createFields();
    this.fields = fields;

    // 1ページあたりの表示行数はCookieから取得する。取得できない場合はデフォルト値を使用する
    const perPage: number | null = this.loadCookie();
    this.perPage = perPage ? perPage : Number(Setting.reservation.perPage);

    // 動的に変更する画面情報を作成する
    this.items = [];
    this.currentRows = this.items.length;

    await this.createView();

    return;
  }

  /**
   * ### 安否確認の列名を作成する
   */
  public createFields(): Array<SafetyConfirmsTableField> {
    const fields: Array<SafetyConfirmsTableField> = [];
    fields.push({ key: "created", label: "作成日", sortable: false, sortDirection: "desc", class: "created text-center" });
    fields.push({ key: "name", label: "タイトル", sortable: false, sortDirection: "desc", class: "confirm-name" });
    fields.push({ key: "otherUser", label: "施設関係者（職員）の安否確認", sortable: false, sortDirection: "desc", class: "other-user" });
    fields.push({ key: "family", label: "家族への安否情報提供", sortable: false, sortDirection: "desc", class: "family" });
    fields.push({ key: "control", label: "操作", sortable: false, sortDirection: "desc", class: "control text-center" });
    return fields;
  }

  /**
   * ### Cookieを見て、値が保持されている場合はCookieの値を返す
   */
  public loadCookie(): number | null {
    if (document.cookie) {
      let cookieMap: any = {};
      let cookies: Array<string> = document.cookie.split("; ");
      cookies.forEach(function (value) {
        //cookie名と値に分ける
        let content = value.split("=");
        cookieMap[content[0]] = content[1];
      });

      // cookieに値が格納されていないとundefinedが格納されているが、実際にはゴミデータが格納されているのでisNaN()でチェックが必要
      const perPage: number = cookieMap["safety-confirm-list-per-page"];

      return isNaN(perPage) ? null : perPage;
    }

    return null;
  }

  /**
   * ### 画面情報作成
   */
  public async createView(): Promise<void> {
    this.isLoading = true;

    // 全体の行数を非同期で取得する
    await this.setRows();

    // 安否確認テーブルに渡す各プロパティを作成する
    const safetyConfirms: Array<SafetyConfirmsGetModel> = await this.getSafetyConfirms();
    const items: Array<SafetyConfirmsTableItem> = this.createItems(safetyConfirms);
    this.items = this.items.concat(items);
    this.currentRows = this.items.length;

    // 現在ページがstoreに存在する場合は復元する
    if (this.$options.name && itemListFilterStore.currentPageMap[this.$options.name] != undefined) {
      this.currentPage = itemListFilterStore.currentPageMap[this.$options.name];
    }

    this.isLoading = false;

    return;
  }

  /**
   * ### ページング用の全件数を設定する
   */
  public async setRows() {
    try {
      const count: number = await this.safetyConfirmsService.getCount(userStore);
      this.rows = count;
    } catch (error) {
      // 本処理はawaitされない非同期処理なので、共通エラーハンドラーが例外をキャッチしない
      // 例外発生時は自分でエラーハンドラーを呼ぶ
      errorHandle(error, this);
    }

    return;
  }

  /**
   * ### 安否確認を取得する
   */
  public async getSafetyConfirms(): Promise<Array<SafetyConfirmsGetModel>> {
    const startIndex: number = this.currentRows;
    // tableのライブラリでソートやフィルタリング、ページングを管理するので表示対象のよりも多めに取得しておく
    const endIndex: number = this.perPage * (this.currentPage * this.pageRange);
    const params: any = {
      startIndex: startIndex,
      endIndex: endIndex,
      order: "desc"
    };

    const response: Array<SafetyConfirmsGetModel> = await this.safetyConfirmsService.gets(userStore, params);

    return response;
  }

  /**
   * ### 安否確認の行を作成する
   */
  public createItems(safetyConfirms: Array<SafetyConfirmsGetModel>): Array<SafetyConfirmsTableItem> {
    let items: Array<SafetyConfirmsTableItem> = [];

    for (const safetyConfirm of safetyConfirms) {
      items.push({
        facilityId: safetyConfirm.facilityId,
        id: safetyConfirm.id,
        name: safetyConfirm.name,
        otherUserMessagesSortKey: safetyConfirm.otherUserMessagesSortKey as string[],
        sendedOtherUserLine: safetyConfirm.sendedOtherUserLine,
        sendedOtherUserMail: safetyConfirm.sendedOtherUserMail,
        acceptingOtherUserReply: safetyConfirm.acceptingOtherUserReply,
        messagesSortKey: safetyConfirm.messagesSortKey as string[],
        acceptingFamilyReply: safetyConfirm.acceptingFamilyReply,
        createUserId: safetyConfirm.createUserId,
        createUserName: safetyConfirm.createUserName,
        deleteFlag: safetyConfirm.deleteFlag,
        created: safetyConfirm.created,
        updated: safetyConfirm.updated
      });
    }

    return items;
  }

  /**
   * ### 1ページあたりの表示行数変更イベント
   *
   * - 1ページあたりの表示行数を更新
   * - ページあたりの表示行数が変わるので表の情報を作り直す
   */
  public async selectPerPage(perPage: number): Promise<void> {
    this.perPage = perPage;
    this.currentPage = 1;
    this.items = [];
    this.currentRows = this.items.length;
    await this.createView();

    return;
  }

  /**
   * ### ページネーションのページ番号押下
   */
  public async selectPage(page: number): Promise<void> {
    this.currentPage = page;

    // 初期表示時点では一定数しか情報を取得していないので、既に情報が取得されていなければ取得する
    const newPageRows = this.perPage * (this.currentPage - 1);
    if (this.currentRows <= newPageRows) {
      await this.createView();
    }

    return;
  }

  /** ### 安否確認削除確認モーダルを表示する */
  public confirmDelete(item: SafetyConfirmsTableItem): void {
    this.deleteTarget = item;
    this.confirmDeleteModalTitle = `${item.name}を削除しますか？`;
    this.showConfirmDeleteModal = true;
    return;
  }

  /** ### 安否確認を削除する */
  public async deleteSafetyConfirm(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "削除中...";

    // 削除APIを呼出す
    await this.safetyConfirmsService.delete(userStore, this.deleteTarget!.id);
    // 一覧から削除したitemを取り除く
    this.items = this.items.concat().filter((item) => {
      if (item.id == this.deleteTarget!.id) {
        return false;
      } else {
        return true;
      }
    });
    this.isBusy = false;
    this.showDeleteSuccessModal = true;
    return;
  }

  /**
   * ### 安否確認登録画面へ遷移する
   */
  public gotoSafetyConfirmRegister(): void {
    this.$router.push(PcUrl.SafetyConfirmRegister);
    return;
  }

  /**
   * ### 家族の事前設定画面へ遷移する
   */
  public gotoFamilySafetyConfirmTemplateEdit(): void {
    this.$router.push(PcUrl.FamilySafetyConfirmTemplateEdit);
    return;
  }

  /**
   * ### 施設関係者の事前設定画面へ遷移する
   */
  public gotoOtherUserSafetyConfirmTemplateEdit(): void {
    this.$router.push(PcUrl.OtherUserSafetyConfirmTemplateEdit);
    return;
  }

  /**
   * ### 施設関係者への安否確認メッセージ送信画面へ遷移する
   */
  public gotoSendOtherUserSafetyConfirm(item: SafetyConfirmsTableItem): void {
    this.$router.push({ name: PcUrl.SendOtherUserSafetyConfirm, params: { id: `${item.id}` } });
    return;
  }

  /**
   * ### 施設関係者への安否確認メッセージ詳細画面へ遷移する
   */
  public gotoOtherUserDetail(item: SafetyConfirmsTableItem): void {
    this.$router.push({ name: PcUrl.OtherUserSafetyConfirmDetail, params: { id: `${item.id}` } });
    return;
  }

  /**
   * ### 家族への安否確認メッセージ詳細画面へ遷移する
   */
  public gotoFamilyDetail(item: SafetyConfirmsTableItem): void {
    this.$router.push({ name: PcUrl.FamilySafetyConfirmDetail, params: { id: `${item.id}` } });
    return;
  }

  beforeRouteLeave(to: Route, from: Route, next: Function) {
    // 画面を離れる前に現在のフィルタリング条件をVuexに保存
    if (this.$options.name) {
      itemListFilterStore.setCurrentPage({ url: this.$options.name, currentPage: this.currentPage });
    }
    next();
  }
}
</script>

<style lang="scss" scoped>
// 親要素からネストしてスタイルを指定する
#safety-confirm-list {
  max-width: 1280px;
  margin-left: auto;
  margin-right: auto;
}
</style>
